import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import type { PasswordResetInfo } from '@ste/shared';
import { ApiRequestError, postJson } from '../../api/auth.ts';
import { API_ROOT } from '../../api/connection.ts';
import { Banner, Button, Spinner } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import logo from '../../theme/assets/logo.svg?raw';
import { inputClass, TotpCodeInput } from '../auth/TotpSetup.tsx';

/** the person who forgot the password sets a new one with the link from STE (ADR-0015) */
export function PasswordResetPage() {
  const { token = '' } = useParams();
  const [info, setInfo] = useState<PasswordResetInfo>();
  const [loadError, setLoadError] = useState<string>();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${API_ROOT}/password-reset/${encodeURIComponent(token)}`)
      .then(async (res) => {
        const body = (await res.json()) as PasswordResetInfo & { message?: string };
        if (!res.ok) throw new Error(body.message ?? t('common.error'));
        setInfo(body);
      })
      .catch((e: Error) => setLoadError(e.message));
  }, [token]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== repeat) return setError(t('reset.mismatch'));
    setBusy(true);
    setError(undefined);
    try {
      await postJson<void>('/password-reset', {
        token,
        password,
        ...(info?.totpRequired ? { totp: code } : {}),
      });
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : t('common.error'));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm space-y-6">
        <div
          className="mx-auto text-text"
          style={{ width: 200 }}
          dangerouslySetInnerHTML={{ __html: logo }}
        />
        {loadError && <Banner tone="danger" title={loadError} />}
        {!info && !loadError && <Spinner label={t('common.loading')} />}
        {done ? (
          <div className="space-y-4">
            <Banner tone="accent" title={t('reset.done')} />
            <Link
              to="/anmelden"
              className="inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-accent px-4 font-medium text-on-accent"
            >
              {t('reset.toLogin')}
            </Link>
          </div>
        ) : (
          info && (
            <form onSubmit={submit} className="space-y-4" noValidate>
              <div>
                <h1 className="text-xl font-semibold">{t('reset.title')}</h1>
                <p className="mt-1 text-sm text-muted">{t('reset.for', { email: info.email })}</p>
              </div>
              {error && <Banner tone="danger" title={error} />}
              {/* lets password managers store the new password under the right account */}
              <input type="hidden" autoComplete="username" value={info.email} readOnly />
              <label className="block">
                <span className="text-sm text-muted">{t('reset.password')}</span>
                <input
                  type="password"
                  className={inputClass}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </label>
              <label className="block">
                <span className="text-sm text-muted">{t('reset.passwordRepeat')}</span>
                <input
                  type="password"
                  className={inputClass}
                  value={repeat}
                  onChange={(e) => setRepeat(e.target.value)}
                  autoComplete="new-password"
                />
              </label>
              {info.totpRequired && (
                <div>
                  <TotpCodeInput value={code} onChange={setCode} />
                  <span className="mt-1 block text-sm text-muted">{t('reset.totpHint')}</span>
                </div>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={busy || password.length < 10 || !repeat || (info.totpRequired && code.length !== 6)}
              >
                {t('reset.submit')}
              </Button>
            </form>
          )
        )}
      </div>
    </div>
  );
}
